import { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import dayjs from 'dayjs';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridDensity,
  GridSortDirection,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridColumnVisibilityModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import { ColumnDensity, components, paths, TASK_PRIORITY } from '@common/types';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import {
  useUpdateWorklistTablePreferencesMutation,
  useWorklistTablePreferencesQuery,
} from '@common/cache';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { formatDateShort } from '@util/date';
import {
  buildGridColumns,
  getCellTextWidth,
  getReorderedColumns,
} from '@util/dataGrid';
import { getDaysPast } from '@util/getDaysPast';
import { getPriorityColor } from '@util/getPriorityColor';
import { PersistedWorklistColumn, theme, useWorklistTableStore } from '@store';
import { convertGridDensityToPreferencesDensity } from 'components/TablePreferences';
import { statusMapper } from 'util/statusMapper.ts';

export type ITaskTableFilters = Pick<
  NonNullable<paths['/task']['get']['parameters']['query']>,
  'sort_by' | 'sort_order' | 'page' | 'limit'
>;

export type ITaskTableEntry = components['schemas']['FilteredListTask'];

type TaskTablePreferences =
  paths['/preferences/task-table']['get']['responses']['200']['content']['application/json'];

type TaskTableColumns = TaskTablePreferences['columns'];

interface IUseTaskTableProps {
  filters: ITaskTableFilters;
  setFilters: (newFilters: ITaskTableFilters) => void;
  loading: boolean;
  isCompletedTab?: boolean;
  onResetAction?: () => void;
}

type BuildTaskColumnsArgs = {
  density?: ColumnDensity;
  isCompletedTab?: boolean;
};

const buildTaskColumns = ({
  density,
  isCompletedTab,
}: BuildTaskColumnsArgs): GridColDef<ITaskTableEntry>[] => {
  const today = new Date();

  return [
    {
      field: 'task',
      headerName: 'Task',
      minWidth: 220,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TruncatedTextWithConditionalTooltip
            text={params.row.task}
            sx={{ fontSize: 14 }}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
          {density !== 'dense' && (
            <TruncatedTextWithConditionalTooltip
              variant='body2'
              color='textSecondary'
              text={params.row.program || 'N/A'}
              sx={{ fontSize: 12 }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 150,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <TruncatedTextWithConditionalTooltip
          variant='body2'
          text={params.row.category}
          sx={{ fontSize: 14 }}
          width={getCellTextWidth(params.colDef.computedWidth)}
        />
      ),
    },
    {
      field: 'patient',
      headerName: 'Patient',
      minWidth: 180,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TruncatedTextWithConditionalTooltip
            text={params.row.patient}
            sx={{ fontSize: 14 }}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
          {density !== 'dense' && (
            <TruncatedTextWithConditionalTooltip
              color='textSecondary'
              variant='body2'
              sx={{ fontSize: 12 }}
              text={
                params.row.patient_dob
                  ? new Date(params.row.patient_dob).toLocaleDateString(
                      'en-US',
                      {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      },
                    )
                  : ''
              }
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 130,
      minWidth: 130,
      sortable: true,
      filterable: false,
      headerAlign: 'center',
      renderCell: params => (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          {!isCompletedTab ? (
            <Chip
              sx={{
                backgroundColor: getPriorityColor(
                  params.row.priority as TASK_PRIORITY,
                ),
                color: theme.palette.common.white,
                height: '24px',
                width: '103px',
              }}
              label={params.row.priority || 'Priority unknown'}
            />
          ) : (
            '-'
          )}
        </Box>
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due date',
      width: 130,
      minWidth: 130,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <TruncatedTextWithConditionalTooltip
          color={
            (today > new Date(params.row.due_date) ||
              //TODO: check if overdue is still a valid status here, since it is missing from the api types enum
              (params.row.status as string) === TASK_PRIORITY.OVERDUE) &&
            params.row.status !== 'Completed' &&
            params.row.status !==
              'Completed (Without requirements completion)' &&
            params.row.status !== 'Archived'
              ? 'error'
              : 'unset'
          }
          variant='body2'
          sx={{ fontSize: 14 }}
          text={formatDateShort(params.row.due_date)}
          width={getCellTextWidth(params.colDef.computedWidth)}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      minWidth: 150,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <TruncatedTextWithConditionalTooltip
          sx={{ fontSize: 14 }}
          color={params.row.status === 'Completed' ? 'success' : 'unset'}
          text={
            params.row.canvas_status ||
            statusMapper({
              status: params.row.status,
              canvas_status: params.row.canvas_status,
            })
          }
          width={getCellTextWidth(params.colDef.computedWidth)}
        />
      ),
    },
    {
      field: 'created_date',
      headerName: 'Created date',
      width: 140,
      minWidth: 140,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minWidth: '100px' }}
        >
          <Typography variant='body1' sx={{ fontSize: '14px' }}>
            {formatDateShort(params.row.created_date)}
          </Typography>
          {density !== 'dense' && (
            <Typography
              variant='body2'
              color='textSecondary'
              sx={{ fontSize: '12px' }}
            >
              {params.row.status !== 'Completed' &&
              params.row.status !==
                'Completed (Without requirements completion)'
                ? getDaysPast(params.row.created_date)
                : ''}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'last_updated_at',
      headerName: 'Last updated',
      width: 150,
      minWidth: 150,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minWidth: '100px' }}
        >
          <Typography variant='body1' sx={{ fontSize: '14px' }}>
            {formatDateShort(params.row.last_updated_at)}
          </Typography>
          {density !== 'dense' && (
            <Typography
              variant='body2'
              color='textSecondary'
              sx={{ fontSize: '12px' }}
            >
              {dayjs(params.row.last_updated_at).format('hh:mm A')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'provider',
      headerName: 'Provider',
      minWidth: 170,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TruncatedTextWithConditionalTooltip
            text={params.row.provider}
            sx={{ fontSize: 14 }}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
          {density !== 'dense' && (
            <TruncatedTextWithConditionalTooltip
              variant='body2'
              sx={{ fontSize: 12 }}
              color='textSecondary'
              text={(params.row.provider_phone || '').replace(
                /(\d{3})(\d{3})(\d{4})/,
                '($1) $2-$3',
              )}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      minWidth: 170,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TruncatedTextWithConditionalTooltip
            text={params.row.location || 'Home'}
            sx={{ fontSize: 14 }}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
          {density !== 'dense' && (
            <TruncatedTextWithConditionalTooltip
              variant='body2'
              color='textSecondary'
              text={params.row.location_operator}
              sx={{ fontSize: 12 }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'assignment',
      headerName: 'Assignment',
      minWidth: 190,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'left',
          }}
        >
          <AccountCircleIcon color='primary' />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pl: '0.5rem',
            }}
          >
            <TruncatedTextWithConditionalTooltip
              text={params.row.assignment}
              sx={{ fontSize: 14 }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                variant='body2'
                color='textSecondary'
                text={params.row.assignment_type}
                sx={{ fontSize: 12 }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </Box>
      ),
    },
  ];
};

const MIN_VISIBLE_COLUMNS = 4;

export const useTaskTable = ({
  filters,
  setFilters,
  isCompletedTab,
  loading,
  onResetAction,
}: IUseTaskTableProps) => {
  const [isResetViewModalOpen, setIsResetViewModalOpen] = useState(false);
  const apiRef = useGridApiRef();
  const { data: preferences } = useWorklistTablePreferencesQuery();
  const updatePreferencesMutation = useUpdateWorklistTablePreferencesMutation();

  const handleUpdatePreferences = async (
    newPreferences: Partial<
      paths['/preferences/task-table']['patch']['requestBody']['content']['application/json']
    >,
  ) => {
    await updatePreferencesMutation.mutateAsync(newPreferences);
  };

  const handleChangeOrder = async (field: string, sort: GridSortDirection) => {
    await updatePreferencesMutation.mutateAsync({
      sort_by:
        field as paths['/preferences/task-table']['patch']['requestBody']['content']['application/json']['sort_by'],
      sort_order: sort ? (sort.toUpperCase() as 'ASC' | 'DESC') : undefined,
    });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    if (loading) return;
    setFilters({
      ...filters,
      limit: model.pageSize,
      page: model.page + 1,
    });

    if (preferences?.page_size !== model.pageSize) {
      updatePreferencesMutation.mutateAsync({
        page_size: model.pageSize,
      });
    }
  };

  const handleUpdateVisibilityModel = (model: GridColumnVisibilityModel) => {
    type TaskColumnKey = keyof TaskTableColumns;

    const currentColumns: TaskTableColumns = preferences?.columns ?? {};
    const updatedColumns: TaskTableColumns = {};

    const fields = Object.keys(currentColumns) as TaskColumnKey[];

    const selectedCount = fields.filter(field => {
      const config = currentColumns[field];
      return config && config.column_display !== 'hidden';
    }).length;

    const totalCount = fields.length;
    const isEmptyModel = Object.keys(model).length === 0;

    const shouldShowAll = isEmptyModel && selectedCount < totalCount;
    const shouldHideAll = isEmptyModel && selectedCount === totalCount;

    for (const field of fields) {
      const config = currentColumns[field];

      if (!config) {
        continue;
      }

      let nextDisplay = config.column_display;

      if (shouldShowAll) {
        if (config.column_display === 'hidden') {
          nextDisplay = 'display';
        }
      } else if (shouldHideAll) {
        nextDisplay = 'hidden';
      } else {
        const isVisible = model[field];
        const wasHidden = config.column_display === 'hidden';

        if (!isVisible) {
          nextDisplay = 'hidden';
        }

        if (isVisible === true && wasHidden) {
          nextDisplay = 'display';
        }
      }

      updatedColumns[field] = {
        ...config,
        column_display: nextDisplay,
      };
    }

    handleUpdatePreferences({ columns: updatedColumns });
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const onDensityChange = (density: GridDensity) => {
    const adaptedDensity = convertGridDensityToPreferencesDensity(density);

    handleUpdatePreferences({
      density: adaptedDensity as ColumnDensity,
    });
  };

  useEffect(() => {
    if (preferences) {
      setFilters({
        ...filters,
        sort_order: preferences.sort_order,
        limit: preferences.page_size ?? 10,
        sort_by: preferences.sort_by,
      });
    }
  }, [preferences]);

  const handleOpenResetViewModal = () => {
    setIsResetViewModalOpen(true);
  };

  const handleCloseResetViewModal = () => {
    setIsResetViewModalOpen(false);
  };

  const persistedColumns = useWorklistTableStore(s => s.columns);
  const setPersistedColumns = useWorklistTableStore(s => s.setColumns);
  const pinnedColumns = useWorklistTableStore(s => s.pinnedColumns);
  const setPinnedColumns = useWorklistTableStore(s => s.setPinnedColumns);
  const resetView = useWorklistTableStore(s => s.resetView);

  const runtimeColumns = buildTaskColumns({
    density: preferences?.density,
    isCompletedTab,
  });

  const columns = buildGridColumns<ITaskTableEntry, PersistedWorklistColumn>(
    runtimeColumns,
    persistedColumns,
  );

  const toPersistedColumn = (
    col: GridColDef<ITaskTableEntry>,
  ): PersistedWorklistColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );

    setPersistedColumns(next.map(toPersistedColumn));
  };

  const visibilityModel: GridColumnVisibilityModel = {};

  Object.entries(preferences?.columns ?? {}).forEach(([field, config]) => {
    visibilityModel[field] = config?.column_display !== 'hidden';
  });

  const visibleColumnsCount =
    Object.values(visibilityModel).filter(Boolean).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;

  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  return {
    apiRef,
    columns: columnsWithHideable,
    preferences,
    pinnedColumns,
    visibilityModel,
    onPageModelChange,
    onDensityChange,
    handleChangeOrder,
    handlePinnedColumnsChange,
    handleUpdateVisibilityModel,
    handleColumnOrderChange,
    handleColumnWidthChange,
    handleFullReset: () => {
      resetView();
      onResetAction?.();
    },
    isResetViewModalOpen,
    handleOpenResetViewModal,
    handleCloseResetViewModal,
    initialSortFilters: {
      sort_order: 'asc',
      sort_by: 'task',
    },
  };
};
